import { useCallback, useEffect, useReducer } from 'react'

type HistoryState<T> = {
  past: T[]
  present: T
  future: T[]
}

type Action<T> =
  | { type: 'UNDO' }
  | { type: 'REDO' }
  | { type: 'SET'; payload: T }
  | { type: 'RESET'; payload?: T }

function reducer<T>(state: HistoryState<T>, action: Action<T>): HistoryState<T> {
  const { past, present, future } = state

  switch (action.type) {
    case 'UNDO': {
      if (past.length === 0) return state

      const previous = past[past.length - 1]
      const newPast = past.slice(0, past.length - 1)

      return {
        past: newPast,
        present: previous,
        future: [present, ...future],
      }
    }

    case 'REDO': {
      if (future.length === 0) return state

      const next = future[0]
      const newFuture = future.slice(1)

      return {
        past: [...past, present],
        present: next,
        future: newFuture,
      }
    }

    case 'SET': {
      if (action.payload === present) return state

      return {
        past: [...past, present],
        present: action.payload,
        future: [],
      }
    }

    case 'RESET': {
      return {
        past: [],
        present: action.payload ?? past[0] ?? present,
        future: [],
      }
    }

    default:
      return state
  }
}

export function useUndoRedo<T extends object>({
  initialValues,
  setFieldValue,
}: {
  initialValues: T
  setFieldValue: (key: keyof T, value: T[keyof T], options?: { dontUpdateMeta: boolean }) => void
}) {
  const [state, dispatch] = useReducer<React.Reducer<HistoryState<T>, Action<T>>>(reducer, {
    past: [],
    present: initialValues,
    future: [],
  })

  useEffect(() => {
    ;(Object.keys(state.present) as Array<keyof T>).forEach((key) => {
      setFieldValue(key, state.present[key], { dontUpdateMeta: true })
    })
  }, [setFieldValue, state.present])

  const canUndo = state.past.length > 0
  const canRedo = state.future.length > 0

  const undo = useCallback(() => {
    if (canUndo) {
      dispatch({ type: 'UNDO' })
    }
  }, [canUndo])

  const redo = useCallback(() => {
    if (canRedo) {
      dispatch({ type: 'REDO' })
    }
  }, [canRedo])

  const set = useCallback(
    (values: Partial<T>) => {
      dispatch({ type: 'SET', payload: { ...state.present, ...values } })
    },
    [state.present],
  )

  const reset = useCallback((newInitialValues?: T) => {
    dispatch({ type: 'RESET', payload: newInitialValues })
  }, [])

  return {
    state: state.present,
    set,
    undo,
    redo,
    reset,
    canUndo,
    canRedo,
  }
}
